import { Helmet } from 'react-helmet-async';
import { toSeoAbsoluteUrl } from '../../utils/seoSiteUrl';
import { validateEventSchema, logEventSchemaValidation } from '../../utils/eventSchemaValidator';

interface EventSchemaProps {
  event: {
    id: string;
    title: string;
    description: string;
    startDate: string;
    endDate?: string;
    location?: {
      name?: string;
      address?: string;
      city?: string;
      postcode?: string;
      country?: string;
    };
    organiser?: {
      name: string;
      url?: string;
    };
    imageUrl?: string;
    price?: number;
    currency?: string;
    eventUrl: string;
  };
}

/**
 * EventSchema component generates JSON-LD structured data for Event pages.
 * Enables rich event cards in search results and better search engine understanding.
 * 
 * ✅ Validates all required fields before rendering
 * ✅ Handles optional fields gracefully
 * ✅ Uses absolute URLs via toSeoAbsoluteUrl
 * ✅ Follows Schema.org Event specification
 */
export default function EventSchema({ event }: EventSchemaProps) {
  // Validate event data before rendering schema
  const validation = validateEventSchema(event);

  if (!validation.isValid) {
    logEventSchemaValidation(event.id, validation);
    // Don't render invalid schema - return null to prevent broken JSON-LD
    return null;
  }

  logEventSchemaValidation(event.id, validation);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    '@id': toSeoAbsoluteUrl(event.eventUrl), // Add unique identifier
    name: event.title,
    description: event.description,
    url: toSeoAbsoluteUrl(event.eventUrl), // Add explicit URL
    startDate: event.startDate,
    ...(event.endDate && { endDate: event.endDate }),
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    ...(event.location && {
      location: {
        '@type': 'Place',
        name: event.location.name || 'Event Venue',
        address: {
          '@type': 'PostalAddress',
          ...(event.location.address && { streetAddress: event.location.address }),
          ...(event.location.city && { addressLocality: event.location.city }),
          ...(event.location.postcode && { postalCode: event.location.postcode }),
          ...(event.location.country && { addressCountry: event.location.country }),
        },
      },
    }),
    ...(event.imageUrl && { image: [toSeoAbsoluteUrl(event.imageUrl)] }),
    ...(event.organiser && {
      organizer: {
        '@type': 'Organization',
        name: event.organiser.name,
        ...(event.organiser.url && { url: toSeoAbsoluteUrl(event.organiser.url) }),
      },
    }),
    ...(event.price !== undefined && {
      offers: {
        '@type': 'Offer',
        price: event.price,
        priceCurrency: event.currency || 'GBP',
        availability: 'https://schema.org/InStock',
        url: toSeoAbsoluteUrl(event.eventUrl),
        validFrom: event.startDate,
      },
    }),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
